import { AfterViewInit, Component, ElementRef, EventEmitter, Injector, Input, OnDestroy, Output, ViewChild, inject, input } from '@angular/core';
import { LDWDocument } from '../../shared/models/LDWDocument';
import { SettingsService } from '../../shared/settings/settings.service';
import { DocumentService } from '../../shared/services/document.service';
import { LdwRouterService } from '../../route/ldw-router.service';
import { MatDialog } from '@angular/material/dialog';
import { UserInformationService } from '../../shell/user-information.service';
import { ActivatedRoute, RouterStateSnapshot } from '@angular/router';
import { SaveOptions, ViewOptions } from '@pdf-tools/four-heights-pdf-web-viewer/es6/pdf-viewer-api/types';
import {
  EraserModule,
  FreetextAnnotationModule,
  HighlightAnnotationModule,
  ImageAnnotationModule,
  InkAnnotationModule,
  PdfWebViewer,
  FileType,
  PdfWebViewerOptionsInterface,
  TextAnnotationModule,
  TextStampSetting, PopupModule
} from '@pdf-tools/four-heights-pdf-web-viewer';
import { AppConfig } from '../../appconfig';
import { CustomStampAnnotationModule } from '../AddOns/CustomStampAnnotationModule';
import { AnnotationService } from '../annotation.service';
import {
  DialogData,
  DialogResult,
  DocumentLeaveDialogComponent,
} from '../../documents/components/document-leave-dialog/document-leave-dialog.component';
import { Observable, firstValueFrom, from, of, startWith, switchMap, withLatestFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { HttpLoadingService } from '../../shell/http-loading.service';
import { ErrorDialogComponent } from '../../shared/dialogs/error-dialog/error-dialog.component';
import { CustomShapeAnnotationModule } from '../AddOns/CustomShapeAnnotationModule';
import { CustomAddSymbolAnnotationModule } from '../AddOns/CustomAddSymbolAnnotationModule';
import { CustomAddCameraImageAnnotationModule } from '../AddOns/CustomAddCameraImageAnnotationModule';
import { toObservable } from '@angular/core/rxjs-interop';

@Component({
  selector: 'ldw-pdft-editor',
  templateUrl: './pdft-editor.component.html',
  styleUrls: ['./pdft-editor.component.scss'],
  standalone: false
})
export class PdftEditorComponent implements AfterViewInit, OnDestroy {
  private dialog = inject(MatDialog);
  private settings = inject(SettingsService);
  private documentService = inject(DocumentService);
  private routerService = inject(LdwRouterService);
  protected userInformationService = inject(UserInformationService);
  private config = inject(AppConfig);
  private annotationService = inject(AnnotationService);
  private activatedRoute = inject(ActivatedRoute);
  private translate = inject(TranslateService);
  private injector = inject(Injector);

  private _document: LDWDocument | undefined = undefined;
  private ldwStamps: TextStampSetting[] = [];
  blob: Blob | undefined = undefined;
  private oldid = '';
  private hasChanges = false;
  private closeNavigationTarget: RouterStateSnapshot | undefined;
  @Input() printAllowed = false;
  @Input() downloadAllowed = false;
  @Input() portofolioAllowed = true;
  @Input() search = '';
  @Input() pageNumber = 0;
  @Input() pageZoom = 0;
  @Input() doCloseNotNavigate = false;
  @Input() isDetail = false;
  @Input() isReadOnly = false;
  planningJob = input<string>()
  @Output() saved = new EventEmitter<Uint8Array>();

  wvInstance?: PdfWebViewer;
  @ViewChild('viewer') viewer!: ElementRef;
  @Output() coreControlsEvent: EventEmitter<string> = new EventEmitter();
  private viewerLoaded = false;
  loading$: Observable<boolean>;


  constructor() {
    const httpLoadingService = inject(HttpLoadingService);

    this.handlePdfViewerLoaded = this.handlePdfViewerLoaded.bind(this);
    this.handleSaveButtonClicked = this.handleSaveButtonClicked.bind(this);
    globalThis.__liveDokWeb__.annotationService = this.annotationService;
    this.loading$ = httpLoadingService.loading$;
  }

  @Input()
  get document(): LDWDocument | undefined {
    return this._document;
  }

  set document(value: LDWDocument | undefined) {
    if (this._document == value) {
      return;
    }
    this._document = value;
    this.openDocument();
  }

  ngAfterViewInit(): void {
    this.annotationService.allStamps$
      .subscribe((res) => {
        this.ldwStamps = res;
        this.initViewer();
      });
  }

  ngOnDestroy() {
    // call destroy to release all the allocated resources
    this.wvInstance?.destroy();

  }

  get userName(): string {
    const userInfo = this.userInformationService.userInformation;

    if (!userInfo.isEmpty) {
      // return `${userInfo.DisplayName} (${userInfo.LoginName}) - ${userInfo.Department}`;
      return `${userInfo.DisplayName}`;
    }
    return '-';
  }

  private handlePdfViewerLoaded() {
    this.viewerLoaded = true;
    if (this._document) {
      this.openDocument();
    }
  }

  private async handleSaveButtonClicked(): Promise<unknown> {
    if (this._document == undefined) {
      return Promise.reject('No document loaded');
    }
    try {
      if (this.config.disableFdfSave()) {
        return await this.SaveAsPdf();
      } else {
        return await this.SaveAsFdf();
      }
    } catch (error) {
      this.dialog.open(ErrorDialogComponent, {
        data: {
          title: 'ERROR.DLG_ERROR_TITLE',
          message: 'DOCUMENT.DLG_SAVEFAILED',
        },
      });
      return Promise.reject(error);
    }
  }

  private async SaveAsFdf() {

    // saveFile returns a Uint8Array
    const options: SaveOptions = { fileType: FileType.Fdf };
    const fileData = await this.wvInstance?.save(options);
    if (fileData) {
      return firstValueFrom(this.documentService.saveFdf(this._document?.id, fileData));
    } else {
      return Promise.reject('No file data');
    }
  }

  private async SaveAsPdf(): Promise<unknown> {
    // saveFile returns a Uint8Array
    const fileData = await this.wvInstance?.save();
    if (fileData) {
      return firstValueFrom(this.documentService.savePdf(this._document?.id, fileData));
    } else {
      return Promise.reject('No file data');
    }
  }

  private openDocument() {
    if (this.viewerLoaded) {
      this.loadDocumentPdf(this._document?.id);
    }
  }

  initViewer(): void {
    let imgDownload = '/assets/download.svg';
    if (this.settings.settings.themes.getSelectedTheme() == 0) {
      imgDownload = '/assets/download-white.svg';

    }
    var customButtons = {};

    if (this.downloadAllowed) {
      customButtons = {
        documentbar: [
          {
            text: this.translate.instant('EDITOR.DOWNLOADPDF'),
            icon: imgDownload,
            onClick: () => this.downloadPdf(),
          },
        ]
      };
    }

    const black = '#000000';
    const white = '#ffffff';
    const red = '#ff0000';
    const blue = '#0000ff';
    const yellow = '#ffff00';
    const green = '#00ff00';
    const magenta = '#ff00ff';
    const cyan = '#00ffff';
    const pink = '#ff00ff';
    const transparent = '#00000000';

    const author = `${this.userName}${this.planningJob() ? ' [' + this.planningJob() + ']' : ''}`

    // options can only be set in the constructor. If you need to change some settings, you must create a new PdfWebViewer
    // @ts-ignore
    const options: Partial<PdfWebViewerOptionsInterface> = {
      viewer: {
        general: {
          user: author,
          language: this.settings.settings.languages.getSelectedlanguageISO(),
          viewOnly: this.isReadOnly,
          promptOnUnsavedChange: false,
        },
        permissions: {
          allowFileDrop: false,
          allowOpenFile: false,
          allowPrinting: this.printAllowed,
          allowSaveFile: !this.isReadOnly,
        },
        customButtons: customButtons,
        callbacks: {
          onSaveFileButtonClicked: this.handleSaveButtonClicked,
        },
      },
      annotation: {
        stamps: this.ldwStamps,
        colors: {
          foregroundColors: [black, red, blue, green, yellow, magenta, cyan, white],
          backgroundColors: [white, yellow, red, green, blue, magenta, cyan, "transparent"],
          highlightColors: [red, yellow, green, pink, blue],
          inkColors: [black, red, blue, green, yellow, magenta, cyan, white],
          textColors: [black, red, blue, green, yellow, magenta, cyan, white],
          defaultHighlightColor: yellow,   // sets color for text highlight
          defaultBackgroundColor: white,   // sets also backgroundcolor in TextAnnotation
          defaultForegroundColor: black,  // sets also Textcolor in TextAnnotation
          defaultInkColor: red,
          freetextBackgroundColor: white,
          shapeColor: red,

          // defaultFreetextBackgroundColor: white,  // ???
        },
        fonts: {
          fontFamilies: ['Helvetica', 'Times', 'Courier', 'Symbol', 'ZapfDingbats'],
          fontSizes: [6, 7, 8, 9, 10, 12, 14, 16, 18, 20, 24, 36, 48, 72],
          defaultFontFamily: 'Helvetica',
          defaultFontSize: 12,
        }
        /*
        colors: {
          foregroundColors: [black,  red,  blue, green, yellow, magenta, cyan, white],
          backgroundColors: [transparent,white, yellow, red, green, blue, magenta, cyan],
          highlightColors: [red, yellow, green, pink, blue],
          inkColors: [black,  red,  blue, green, yellow, magenta, cyan, white],
          textColors: [black,  red,  blue, green, yellow, magenta, cyan, white],
          defaultForegroundColor: black,
          defaultInkColor: black,
          defaultHighlightColor: yellow,
          defaultBackgroundColor: transparent,
          defaultHighlightAnnotationColor: yellow,
          defaultFreetextBackgroundColor: transparent,
          freetextBackgroundColor: transparent,
          defaultStickyNoteColor: yellow,
        },

         */
      },
      modules: [
        PopupModule,
        TextAnnotationModule,
        InkAnnotationModule,
        EraserModule,
        FreetextAnnotationModule,
        HighlightAnnotationModule,
        // ShapeAnnotationModule,
        CustomShapeAnnotationModule,
        CustomAddSymbolAnnotationModule,
        ImageAnnotationModule,
        CustomAddCameraImageAnnotationModule
      ],
    };

    if (this.ldwStamps.length) {
      options.modules?.push(CustomStampAnnotationModule);
    }

    this.wvInstance = new PdfWebViewer(this.viewer.nativeElement, this.config.editorLicence(), options);

    if (this.settings.settings.themes.getSelectedTheme() == 0) {
      document.body.setAttribute('data-theme', 'dark');
    } else {
      document.body.setAttribute('data-theme', 'light');
    }

    this.wvInstance.addEventListener('appLoaded', this.handlePdfViewerLoaded);
    this.wvInstance.addEventListener('pageNumberChanged', () =>
      this.routerService.setQueryParams('page', this.wvInstance?.getPageNumber()?.toString() ?? '1')
    );
    this.wvInstance.addEventListener('zoomChanged', () =>
      this.routerService.setQueryParams('zoom', this.wvInstance?.getZoom()?.toString() ?? '1')
    );

    toObservable(this.planningJob, { injector: this.injector }).pipe(startWith(this.planningJob()))
      .subscribe(() => {
        const author = `${this.userName}${this.planningJob() ? ' [' + this.planningJob() + ']' : ''}`;
        globalThis.__liveDokWeb__.author = author;
      })

  }

  async downloadPdf() {
    const saveOptions = { fileType: FileType.Pdf };

    return await this.wvInstance?.downloadFile(saveOptions)?.then();
  }

  loadDocumentPdf(docId: string | undefined) {
    if (docId == undefined) {
      return;
    }
    if (docId == this.oldid) {
      return;
    }
    const { page, zoom } = this.activatedRoute.snapshot.queryParams;
    this.oldid = docId;
    this.documentService.getPdf(docId).subscribe((res) => {
      this.blob = res;
      this.hasChanges = false;
      //this.wvInstance = false;
      //      const viewOptions: ViewOptions = { initialPageNumber: this.pageNumber, initialZoom: this.pageZoom };
      const viewOptions: ViewOptions = {
        initialPageNumber: this.pageNumber || +page || 1,
        initialZoom: this.pageZoom || +zoom || 1,
      };
      this.wvInstance?.open({ data: this.blob }, null, null, viewOptions);
    });
  }

  /*
  saveDocument(annotationManager: Core.AnnotationManager, documentViewer: Core.DocumentViewer) {
    if (this.document === undefined) {
      return Promise.reject('No document loaded');
    }
    const docId = this.document.id;
    return new Promise((resolve) => {

      annotationManager.exportAnnotations().then((xfdfString: any) => {

        documentViewer.getDocument().getFileData({ xfdfString }).then((data) => {
          const arr = new Uint8Array(data);
          const blob = new Blob([arr], { type: 'application/pdf' });
          this.documentService.savePdf(docId, blob).subscribe(result => {resolve(result)});
        });
      });
    });
  }

   */

  public checkCanClose(): Observable<boolean> {
    if (!this.wvInstance?.hasChanges()) {
      return of(true);
    }
    return this.dialog
      .open<DocumentLeaveDialogComponent, DialogData, DialogResult>(DocumentLeaveDialogComponent, {
        data: {
          saveAction: () => {
            return from(this.handleSaveButtonClicked());
          },
          discardAction: () => {
            if (this.wvInstance && this.wvInstance.destroy) {
              this.wvInstance.destroy();
            }
            return of(true);
          },
        } as DialogData,
      })
      .afterClosed()
      .pipe(switchMap((result) => of(result?.closeDocument || false)));
  }

  checkCanCloseNavigate(state?: RouterStateSnapshot) {
    return this.checkCanClose();
  }
}
